import { useState } from 'react';

import { Box, Divider, Flex } from '@chakra-ui/react';
import { t, Trans } from '@lingui/macro';
import cloneDeep from 'lodash.clonedeep';
import { useParams } from 'react-router-dom';
import { useEffectOnce, useUpdateEffect } from 'usehooks-ts';

import { apiGetShopResourceDetail, ROUTE_PAGE_SHOP } from '../../../constants';
import useFetch from '../../../hooks/useFetch';
import useFormShopResource from '../../../hooks/useFormShopResource';
import useIsMobile from '../../../hooks/useIsMobile';
import useNavigateBookingEngine from '../../../hooks/useNavigateBookingEngine';
import { FIRST_NAME_FIELDNAME, LAST_NAME_FIELDNAME } from '../../../hooks/useReadAdditionalData';
import useWritePreCart from '../../../hooks/useWritePreCart';
import { REQUEST_STATUS } from '../../../utils/requestUtil';
import { BOTTOM_ROUNDED_CONTAINER_PROPS, COMMON_PADDING, RESPONSIVE_WIDTH_BOX_RIGHT, TOP_ROUNDED_CONTAINER_PROPS } from '../../../utils/styleUtil';
import AlertError from '../../generic/error/AlertError';
import FetchLoaderSpinner from '../../generic/FetchLoaderSpinner';
import Image from '../../generic/Image';
import ContainerLeft from '../../generic/layout/ContainerLeft';
import ContainerTotal from '../../generic/layout/ContainerTotal';
import FlexRow from '../../generic/layout/FlexRow';
import SessionVariantsActions from '../sessionvariants/SessionVariantsActions';
import ShopResourceBookByName from './ShopResourceBookByName';
import ShopResourceBookByQuantity from './ShopResourceBookByQuantity';
import ShopResourceBookingErrors from './ShopResourceBookingErrors';
import ShopResourceDetail from './ShopResourceDetail';

const ShopResourceBookingForm = () => {
  const isMobile = useIsMobile();

  const { resourceId } = useParams();
  const { data, status } = useFetch(apiGetShopResourceDetail(resourceId));

  const { preCartData, setPreCartShopResource } = useWritePreCart();
  const { shopResource } = preCartData;
  const { onSubmitAddToCart, statusAddCart, requestedAction, setRequestedAction } = useFormShopResource(preCartData);

  const [stockUnavailable, setStockUnavailable] = useState(false);

  const ShopBookingComponent = shopResource && shopResource.nominative ? ShopResourceBookByName : ShopResourceBookByQuantity;

  const navigate = useNavigateBookingEngine();
  useEffectOnce(() => {
    if (!resourceId) {
      navigate(ROUTE_PAGE_SHOP);
    }
  });

  useUpdateEffect(() => {
    data.resource.quantity = 1;
    data.resource.defaultNamingData = [{ [FIRST_NAME_FIELDNAME]: '', [LAST_NAME_FIELDNAME]: '' }];
    const copyNamingData = cloneDeep(data.resource.defaultNamingData);
    data.resource.namingData = copyNamingData;
    setPreCartShopResource(data.resource, data.child_resources);
  }, [data]);

  const setShopResource = (quantity, namingData) => {
    if (quantity < 0) return;

    const isStockUnavailable = shopResource.stock != null && quantity > shopResource.stock;
    setStockUnavailable(isStockUnavailable);

    const copyResource = cloneDeep(shopResource);
    copyResource.quantity = quantity;
    copyResource.namingData = namingData || null;
    setPreCartShopResource(copyResource, data.child_resources);
  };

  if (!resourceId) return <></>;
  return (
    <form onSubmit={onSubmitAddToCart}>
      <FlexRow>
        <ContainerLeft title={<Trans>Shop</Trans>}>
          <Box bg="brand.background_main" p={COMMON_PADDING} pt="0" {...BOTTOM_ROUNDED_CONTAINER_PROPS}>
            <FetchLoaderSpinner status={status} />
            {status === REQUEST_STATUS.ERROR && <AlertError />}

            {data && shopResource && (
              <>
                <ShopResourceDetail shopResource={shopResource} />
                <Divider m=".5rem 0" />

                <Flex gap="1rem">
                  <ShopBookingComponent shopResource={shopResource} setShopResource={setShopResource} data={data} dataWidthProps={dataWidthProps} />
                </Flex>

                <ShopResourceBookingErrors shopResource={shopResource} stockUnavailable={stockUnavailable} statusAddCart={statusAddCart} />
              </>
            )}
          </Box>
        </ContainerLeft>

        <Box h="100%" bg="brand.background_main" {...RESPONSIVE_WIDTH_BOX_RIGHT} {...TOP_ROUNDED_CONTAINER_PROPS} {...BOTTOM_ROUNDED_CONTAINER_PROPS}>
          {data && shopResource && (
            <>
              <Image imageUrl={shopResource.description_image} alt={t`Resource image`} />
              <ContainerTotal
                total={shopResource.nominative ? data.resource.price * shopResource.namingData.length : data.resource.price * shopResource.quantity}
              />
              <SessionVariantsActions
                statusAddCart={statusAddCart}
                requestedAction={requestedAction}
                setRequestedAction={setRequestedAction}
                isMobile={isMobile}
                statusLoadVariants={REQUEST_STATUS.OK}
                //   itemsToDeleteForEdit={itemsToDeleteForEdit}
              />
            </>
          )}
        </Box>
      </FlexRow>
    </form>
  );
};

export default ShopResourceBookingForm;

const dataWidthProps = {
  minWidth: { base: '115px', sm: '115px' },
  maxWidth: { base: '200px', sm: '115px' },
};
